import { cn } from '@/lib/utils';

import type { TransmissionHistoryEntry } from '../request/cards/followups/hooks/useFollowUpTransmission';

interface FollowUpDotsProps {
  /** History entries to display with status-based colors */
  history: TransmissionHistoryEntry[];
  /** Total number of transmission slots */
  total: number;
  size?: 'xs' | 'sm';
}

/**
 * Get dot color based on transmission status
 */
const getStatusColor = (status: string | undefined): string => {
  const normalized = status?.toLowerCase() || '';
  if (['delivered', 'success', 'completed'].includes(normalized)) {
    return 'bg-green-500';
  }
  if (['sent', 'pending', 'queued'].includes(normalized)) {
    return 'bg-yellow-500';
  }
  if (['failed', 'error', 'rejected'].includes(normalized)) {
    return 'bg-red-500';
  }
  return 'bg-yellow-500';
};

/**
 * Premium visual indicator showing follow-up transmission progress.
 * - Green dots for delivered transmissions
 * - Yellow dots for sent/pending transmissions
 * - Red dots for failed transmissions
 * - Gray dots for future slots
 */
export const FollowUpDots = ({
  history,
  total,
  size = 'sm',
}: FollowUpDotsProps) => {
  const dotSize = size === 'xs' ? 'w-1.5 h-1.5' : 'w-2 h-2';

  // Sort history by date (oldest first) to match timeline order
  const sortedHistory = [...history].sort(
    (a, b) =>
      new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
  );

  return (
    <div className="flex items-center gap-0.5">
      {Array.from({ length: total }).map((_, i) => {
        const entry = sortedHistory[i];
        return (
          <div
            key={entry?.id || `pending-${i}`}
            className={cn(
              'rounded-full transition-colors',
              dotSize,
              entry ? getStatusColor(entry.status) : 'bg-zinc-300',
            )}
          />
        );
      })}
    </div>
  );
};
